import React, { useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  styled,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@emotion/react";

const StyledTypography = styled(Typography)({
  display: "flex",
  alignItems: "center",
  fontSize: "14px",
  color: "#0D0D0E",
  fontWeight: 500,
  gap: "5px",
  flex: 1,
  flexGrow: 0,
  width: "100%",
});

const projectColors = {
  "Interior Design": "#CC5F29",
  Landscaping: "#96CC29",
  Airbnb: "#29CC96",
  "Home Renovation": "#2929CC",
  Kitchen: "#CC29B1",
  Bathroom: "#CC2929",
  Rooms: "#9629CC",
  Retail: "#c41d7f",
  Commercial: "#096dd9",
  Villa: "#ffad5c",
  Apartment: "#e8e6ef",
};

const projectNames = {
  "Interior Design": "Interior",
  Landscaping: "Landscaping",
  Airbnb: "Airbnb",
  "Home Renovation": "Homes",
  Kitchen: "Kitchen",
  Bathroom: "Bathroom",
  Rooms: "Rooms",
  Commercial: "Commercial",
  Retail: "Retail",
  Villa: "Villa",
  Apartment: "Apartment",
};

const LinearStats = ({ summary = {} }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const projectEntries = useMemo(() => Object.entries(summary), [summary]);
  const totalValue = useMemo(
    () => projectEntries.reduce((sum, [, value]) => sum + value, 0),
    [projectEntries]
  );

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      padding="0"
      marginTop={!isMobile && "2vh"}
      width="100%"
    >
      <Box width="100%">
        <Paper
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            boxShadow: "none",
            borderRadius: 0,
            padding: "1vh 0vw",
            background: "transparent",
          }}
        >
          <Box
            sx={{
              display: "flex",
              width: "100%",
              background: "transparent",
              gap: ".7vw",
            }}
          >
            {projectEntries.map(([project, value]) => (
              <Box
                key={project}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  flex: 1,
                  justifyContent: "flex-start",
                  "&:last-child": {
                    justifyContent: "center",
                  },
                }}
              >
                <StyledTypography
                  style={{ color: "#949DAA", fontSize: "12px" }}
                >
                  {projectNames[project]}
                </StyledTypography>
              </Box>
            ))}
          </Box>

          <Box
            sx={{
              display: "flex",
              width: "100%",
              background: "transparent",
            }}
          >
            {projectEntries.map(([project, value]) => (
              <Box
                key={project}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  flex: 1,
                  justifyContent: "flex-start",
                  "&:last-child": {
                    justifyContent: "center",
                  },
                }}
              >
                <Box
                  sx={{
                    width: "10px",
                    height: "10px",
                    borderRadius: "50%",
                    backgroundColor: projectColors[project] || "transparent",
                    cursor: "pointer",
                  }}
                />
                <StyledTypography>{value}</StyledTypography>
              </Box>
            ))}
          </Box>

          <Box
            sx={{
              display: "flex",
              width: "100%",
              height: 12,
              borderRadius: 6,
              backgroundColor: "#fff",
              overflow: "hidden",
              marginTop: "2vh",
              gap: "2px",
            }}
          >
            {projectEntries.map(([project, value]) => (
              <Tooltip key={project} title={project} arrow>
                <Box
                  sx={{
                    width: `${(value / totalValue) * 100}%`,
                    backgroundColor: projectColors[project] || "#000",
                    borderRadius: "5px",
                    cursor: "pointer",
                  }}
                />
              </Tooltip>
            ))}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default React.memo(LinearStats);
